import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Pencil, X, Zap, Clock, CalendarClock } from 'lucide-react';

const enconOrange = '#ee7203';

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  applied: 'bg-green-50 text-green-700 border-green-200',
  cancelled: 'bg-gray-100 text-gray-400 border-gray-200',
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  } catch {
    return d;
  }
};

/** Add/Edit dialog for a scheduled increase. In "edit" mode the scope/supplier/
 * category are fixed (only pct/date/note change) since changing scope on an
 * already-created entry would be ambiguous. */
const ScheduledIncreaseDialog = ({ open, onClose, onSaved, suppliers, editing }) => {
  const [scope, setScope] = useState('supplier');
  const [supplierId, setSupplierId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [pct, setPct] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setScope(editing.scope);
      setSupplierId(editing.supplier_id);
      setCategoryId(editing.category_id || '');
      setPct(String(editing.pct));
      setEffectiveDate(editing.effective_date);
      setNote(editing.note || '');
    } else {
      setScope('supplier');
      setSupplierId(suppliers[0]?.id || '');
      setCategoryId('');
      setPct('');
      setEffectiveDate(new Date().toISOString().slice(0, 10));
      setNote('');
    }
  }, [open, editing, suppliers]);

  useEffect(() => {
    if (!open || editing || scope !== 'category' || !supplierId) { setCategories([]); return; }
    setLoadingCategories(true);
    api.get(`${API}/pricing/suppliers/${supplierId}/categories`)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoadingCategories(false));
  }, [open, editing, scope, supplierId]);

  const save = async () => {
    if (!pct) { toast.error('Enter a percentage'); return; }
    if (!editing && scope === 'category' && !categoryId) { toast.error('Choose a category'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`${API}/pricing/scheduled-increases/${editing.id}`, {
          pct: parseFloat(pct) || 0, effective_date: effectiveDate, note,
        });
        toast.success('Scheduled increase updated');
      } else {
        const res = await api.post(`${API}/pricing/scheduled-increases`, {
          scope, supplier_id: supplierId, category_id: scope === 'category' ? categoryId : null,
          pct: parseFloat(pct) || 0, effective_date: effectiveDate, note,
        });
        toast.success(res.data.status === 'applied'
          ? `Applied now — ${res.data.updated_count} price(s) updated`
          : `Scheduled for ${fmtDate(res.data.effective_date)}`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="scheduled-increase-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Scheduled Increase' : 'New Scheduled Increase'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <>
              <div>
                <Label className="text-xs text-gray-600">Applies to</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setScope('supplier')}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${scope === 'supplier' ? 'text-white' : 'text-gray-500 border-gray-200'}`}
                    style={scope === 'supplier' ? { backgroundColor: enconOrange, borderColor: enconOrange } : {}}
                    data-testid="scheduled-increase-scope-supplier"
                  >
                    Whole Supplier
                  </button>
                  <button
                    type="button"
                    onClick={() => setScope('category')}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${scope === 'category' ? 'text-white' : 'text-gray-500 border-gray-200'}`}
                    style={scope === 'category' ? { backgroundColor: enconOrange, borderColor: enconOrange } : {}}
                    data-testid="scheduled-increase-scope-category"
                  >
                    One Category
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Supplier</Label>
                <select
                  value={supplierId}
                  onChange={(e) => { setSupplierId(e.target.value); setCategoryId(''); }}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white"
                  data-testid="scheduled-increase-supplier-select"
                >
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {scope === 'category' && (
                <div>
                  <Label className="text-xs text-gray-600">Category</Label>
                  {loadingCategories ? (
                    <div className="py-2"><Loader2 className="w-4 h-4 animate-spin" style={{ color: enconOrange }} /></div>
                  ) : (
                    <select
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white"
                      data-testid="scheduled-increase-category-select"
                    >
                      <option value="">Choose a category...</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
              )}
            </>
          )}
          <div>
            <Label className="text-xs text-gray-600">Percentage Increase (%)</Label>
            <Input type="number" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="e.g. 5" data-testid="scheduled-increase-pct-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Effective Date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="scheduled-increase-date-input" />
            <p className="text-[11px] text-gray-400 mt-1">Today or earlier applies immediately. A future date is held as pending until that day.</p>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="scheduled-increase-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            style={{ backgroundColor: enconOrange }}
            className="text-white"
            onClick={save}
            disabled={saving}
            data-testid="scheduled-increase-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PricingScheduledIncreasesTab = () => {
  const [entries, setEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [entriesRes, suppliersRes] = await Promise.all([
        api.get(`${API}/pricing/scheduled-increases`),
        api.get(`${API}/pricing/suppliers`),
      ]);
      setEntries(entriesRes.data?.entries || []);
      setSuppliers(suppliersRes.data?.suppliers || []);
    } catch {
      toast.error('Failed to load scheduled increases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const rank = { pending: 0, applied: 1, cancelled: 2 };
    return [...entries].sort((a, b) => (rank[a.status] - rank[b.status]) || a.effective_date.localeCompare(b.effective_date));
  }, [entries]);

  const cancel = async (entry) => {
    if (!window.confirm(`Cancel the ${entry.pct}% increase scheduled for ${fmtDate(entry.effective_date)}?`)) return;
    setBusyId(entry.id);
    try {
      await api.delete(`${API}/pricing/scheduled-increases/${entry.id}`);
      toast.success('Cancelled');
      load();
    } catch {
      toast.error('Failed to cancel');
    } finally {
      setBusyId(null);
    }
  };

  const applyNow = async (entry) => {
    if (!window.confirm(`Apply this ${entry.pct}% increase right now instead of waiting for ${fmtDate(entry.effective_date)}?`)) return;
    setBusyId(entry.id);
    try {
      const res = await api.post(`${API}/pricing/scheduled-increases/${entry.id}/apply-now`);
      toast.success(`Applied — ${res.data.updated_count} price(s) updated`);
      load();
    } catch {
      toast.error('Failed to apply');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="pricing-scheduled-increases-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 flex items-center gap-1.5">
          <CalendarClock className="w-4 h-4" style={{ color: enconOrange }} />
          Increases scheduled for a future date apply automatically that day — see and edit them here before they fire.
        </p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} style={{ backgroundColor: enconOrange }} className="text-white" size="sm" data-testid="add-scheduled-increase-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Scheduled Increase
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>No scheduled increases yet — use "Add Scheduled Increase" to set one up for a future date.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {sorted.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-3 rounded-xl border"
              data-testid={`scheduled-increase-row-${entry.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Badge variant="outline" className={`${STATUS_STYLE[entry.status]} capitalize shrink-0`} data-testid={`scheduled-increase-status-${entry.id}`}>
                  {entry.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                  {entry.status}
                </Badge>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {entry.supplier_name}
                    {entry.category_name ? <span className="text-gray-400"> · {entry.category_name}</span> : <span className="text-gray-400"> · All categories</span>}
                  </p>
                  {entry.note && <p className="text-xs text-gray-400 truncate">{entry.note}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-sm font-bold" style={{ color: enconOrange }}>+{entry.pct}%</span>
                <span className="text-xs text-gray-500 w-20 text-right">{fmtDate(entry.effective_date)}</span>
                {entry.status === 'applied' && entry.updated_count != null && (
                  <span className="text-xs text-gray-400 w-24">{entry.updated_count} updated</span>
                )}
                {entry.status === 'pending' && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => applyNow(entry)}
                      disabled={busyId === entry.id}
                      className="p-1.5 rounded hover:bg-orange-50 text-orange-500"
                      title="Apply now"
                      data-testid={`scheduled-increase-apply-now-${entry.id}`}
                    >
                      <Zap className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { setEditing(entry); setDialogOpen(true); }}
                      disabled={busyId === entry.id}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      title="Edit"
                      data-testid={`scheduled-increase-edit-${entry.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => cancel(entry)}
                      disabled={busyId === entry.id}
                      className="p-1.5 rounded hover:bg-red-50 text-red-400"
                      title="Cancel"
                      data-testid={`scheduled-increase-cancel-${entry.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ScheduledIncreaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        suppliers={suppliers}
        editing={editing}
      />
    </div>
  );
};

export default PricingScheduledIncreasesTab;
